<div class="space-y-6 container mx-auto pb-12">
  <!-- Header with Title and Refresh Action -->
  <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div>
      <div class="flex items-center gap-3">
        <h1 class="text-2xl font-bold text-base-content">
          Workspace Usage & Limits
        </h1>
        @if (usageData()?.plan?.name) {
          <span
            class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold"
          >
            {{ usageData()?.plan?.name }}
          </span>
        }
      </div>
      <p class="text-sm text-base-content/60 mt-1">
        Real-time resource utilization and capacity for
        {{ currentTenant?.name || 'this workspace' }}.
      </p>
    </div>

    <button
      class="daisy-btn daisy-btn-outline daisy-btn-sm gap-2"
      (click)="loadUsage()"
      [disabled]="loading()"
    >
      @if (loading()) {
        <span
          class="daisy-loading daisy-loading-spinner daisy-loading-xs"
        ></span>
      } @else {
        <i class="fa-solid fa-arrows-rotate"></i>
      }
      Refresh
    </button>
  </div>

  <!-- Informational Banner: How Real-Time Tracking & Recounting Works -->
  <div
    class="daisy-alert daisy-alert-info bg-info/10 border border-info/20 text-base-content text-sm shadow-sm"
  >
    <i class="fa-solid fa-circle-info text-info text-lg shrink-0"></i>
    <div>
      <h3 class="font-bold text-xs uppercase tracking-wider text-info">
        Dynamic Real-Time Recounting
      </h3>
      <p class="text-xs text-base-content/80 mt-0.5 leading-relaxed">
        Usage quotas update automatically in real-time. Archiving completed live
        classes, deleting unused media files, or removing inactive members will
        <strong>immediately reduce your usage and free up quota</strong>.
      </p>
    </div>
  </div>

  <!-- Loading State -->
  @if (loading() && !usageData()) {
    <div class="flex flex-col items-center justify-center py-20 gap-3">
      <span
        class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"
      ></span>
      <p class="text-sm text-base-content/60">
        Fetching latest workspace metrics...
      </p>
    </div>
  }

  <!-- Error State -->
  @if (error()) {
    <div class="daisy-alert daisy-alert-error shadow-sm">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <span>{{ error() }}</span>
      <button
        class="daisy-btn daisy-btn-xs daisy-btn-outline"
        (click)="loadUsage()"
      >
        Retry
      </button>
    </div>
  }

  <!-- Metric Cards Grid -->
  @if (usageData(); as data) {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Card 1: Instructors & Tutors -->
      <div
        class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow transition-all duration-200"
      >
        <div class="daisy-card-body p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary"
              >
                <i class="fa-solid fa-user-tie text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-base-content">
                  Instructors & Tutors
                </h2>
                <p class="text-xs text-base-content/60">
                  Active instructors + pending invites
                </p>
              </div>
            </div>
            @if (!data.usage.instructors.unlimited) {
              <span
                class="daisy-badge daisy-badge-sm font-semibold"
                [ngClass]="getBadgeColorClass(data.usage.instructors.percent)"
              >
                {{ data.usage.instructors.percent }}%
              </span>
            } @else {
              <span
                class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold"
                >Unlimited</span
              >
            }
          </div>

          <div class="mt-4 flex items-baseline justify-between">
            <span class="text-3xl font-bold text-base-content">
              {{ data.usage.instructors.used }}
            </span>
            <span class="text-sm text-base-content/60">
              @if (data.usage.instructors.unlimited) {
                Quota: Unlimited
              } @else {
                of {{ data.usage.instructors.limit }} allowed
              }
            </span>
          </div>

          @if (!data.usage.instructors.unlimited) {
            <progress
              class="daisy-progress w-full mt-3 h-2"
              [ngClass]="getProgressColorClass(data.usage.instructors.percent)"
              [value]="data.usage.instructors.percent"
              max="100"
            ></progress>
          } @else {
            <progress
              class="daisy-progress daisy-progress-success w-full mt-3 h-2"
              value="100"
              max="100"
            ></progress>
          }

          <div class="mt-2 text-xs text-base-content/50 flex justify-between">
            <span>
              {{
                data.usage.instructors.unlimited
                  ? 'No restriction'
                  : data.usage.instructors.limit -
                    data.usage.instructors.used +
                    ' spots available'
              }}
            </span>
            @if (data.usage.instructors.percent >= 100) {
              <span class="text-error font-medium">Limit reached</span>
            }
          </div>
        </div>
      </div>

      <!-- Card 2: Live Classes -->
      <div
        class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow transition-all duration-200"
      >
        <div class="daisy-card-body p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary"
              >
                <i class="fa-solid fa-video text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-base-content">
                  Active Live Classes
                </h2>
                <p class="text-xs text-base-content/60">
                  Scheduled, published, or draft classes
                </p>
              </div>
            </div>
            @if (!data.usage.liveClasses.unlimited) {
              <span
                class="daisy-badge daisy-badge-sm font-semibold"
                [ngClass]="getBadgeColorClass(data.usage.liveClasses.percent)"
              >
                {{ data.usage.liveClasses.percent }}%
              </span>
            } @else {
              <span
                class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold"
                >Unlimited</span
              >
            }
          </div>

          <div class="mt-4 flex items-baseline justify-between">
            <span class="text-3xl font-bold text-base-content">
              {{ data.usage.liveClasses.used }}
            </span>
            <span class="text-sm text-base-content/60">
              @if (data.usage.liveClasses.unlimited) {
                Quota: Unlimited
              } @else {
                of {{ data.usage.liveClasses.limit }} allowed
              }
            </span>
          </div>

          @if (!data.usage.liveClasses.unlimited) {
            <progress
              class="daisy-progress w-full mt-3 h-2"
              [ngClass]="getProgressColorClass(data.usage.liveClasses.percent)"
              [value]="data.usage.liveClasses.percent"
              max="100"
            ></progress>
          } @else {
            <progress
              class="daisy-progress daisy-progress-success w-full mt-3 h-2"
              value="100"
              max="100"
            ></progress>
          }

          <div class="mt-2 text-xs text-base-content/50 flex justify-between">
            <span>
              {{
                data.usage.liveClasses.unlimited
                  ? 'No restriction'
                  : data.usage.liveClasses.limit -
                    data.usage.liveClasses.used +
                    ' active classes remaining'
              }}
            </span>
            @if (data.usage.liveClasses.percent >= 100) {
              <span class="text-error font-medium"
                >Limit reached (archive to free up)</span
              >
            }
          </div>
        </div>
      </div>

      <!-- Card 3: Active Students -->
      <div
        class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow transition-all duration-200"
      >
        <div class="daisy-card-body p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center text-accent"
              >
                <i class="fa-solid fa-user-graduate text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-base-content">Active Students</h2>
                <p class="text-xs text-base-content/60">
                  Registered students in this workspace
                </p>
              </div>
            </div>
            @if (!data.usage.activeStudents.unlimited) {
              <span
                class="daisy-badge daisy-badge-sm font-semibold"
                [ngClass]="
                  getBadgeColorClass(data.usage.activeStudents.percent)
                "
              >
                {{ data.usage.activeStudents.percent }}%
              </span>
            } @else {
              <span
                class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold"
                >Unlimited</span
              >
            }
          </div>

          <div class="mt-4 flex items-baseline justify-between">
            <span class="text-3xl font-bold text-base-content">
              {{ data.usage.activeStudents.used }}
            </span>
            <span class="text-sm text-base-content/60">
              @if (data.usage.activeStudents.unlimited) {
                Quota: Unlimited
              } @else {
                of {{ data.usage.activeStudents.limit }} allowed
              }
            </span>
          </div>

          @if (!data.usage.activeStudents.unlimited) {
            <progress
              class="daisy-progress w-full mt-3 h-2"
              [ngClass]="
                getProgressColorClass(data.usage.activeStudents.percent)
              "
              [value]="data.usage.activeStudents.percent"
              max="100"
            ></progress>
          } @else {
            <progress
              class="daisy-progress daisy-progress-success w-full mt-3 h-2"
              value="100"
              max="100"
            ></progress>
          }

          <div class="mt-2 text-xs text-base-content/50 flex justify-between">
            <span>
              {{
                data.usage.activeStudents.unlimited
                  ? 'No restriction'
                  : data.usage.activeStudents.limit -
                    data.usage.activeStudents.used +
                    ' enrollments remaining'
              }}
            </span>
            @if (data.usage.activeStudents.percent >= 100) {
              <span class="text-error font-medium">Limit reached</span>
            }
          </div>
        </div>
      </div>

      <!-- Card 4: Storage Space -->
      <div
        class="daisy-card bg-base-100 border border-base-300 shadow-sm hover:shadow transition-all duration-200"
      >
        <div class="daisy-card-body p-6">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center text-info"
              >
                <i class="fa-solid fa-hard-drive text-lg"></i>
              </div>
              <div>
                <h2 class="font-semibold text-base-content">Storage Space</h2>
                <p class="text-xs text-base-content/60">
                  Recordings, video assets, and documents
                </p>
              </div>
            </div>
            @if (!data.usage.storage.unlimited) {
              <span
                class="daisy-badge daisy-badge-sm font-semibold"
                [ngClass]="getBadgeColorClass(data.usage.storage.percent)"
              >
                {{ data.usage.storage.percent }}%
              </span>
            } @else {
              <span
                class="daisy-badge daisy-badge-success daisy-badge-sm font-semibold"
                >Unlimited</span
              >
            }
          </div>

          <div class="mt-4 flex items-baseline justify-between">
            <span class="text-3xl font-bold text-base-content">
              {{ data.usage.storage.usedGB }}
              <span class="text-sm font-normal text-base-content/70">GB</span>
            </span>
            <span class="text-sm text-base-content/60">
              @if (data.usage.storage.unlimited) {
                Quota: Unlimited
              } @else {
                of {{ data.usage.storage.limitGB }} GB allowed
              }
            </span>
          </div>

          @if (!data.usage.storage.unlimited) {
            <progress
              class="daisy-progress w-full mt-3 h-2"
              [ngClass]="getProgressColorClass(data.usage.storage.percent)"
              [value]="data.usage.storage.percent"
              max="100"
            ></progress>
          } @else {
            <progress
              class="daisy-progress daisy-progress-success w-full mt-3 h-2"
              value="100"
              max="100"
            ></progress>
          }

          <div class="mt-2 text-xs text-base-content/50 flex justify-between">
            <span>
              {{
                data.usage.storage.unlimited
                  ? 'No restriction'
                  : (
                      data.usage.storage.limitGB - data.usage.storage.usedGB
                    ).toFixed(2) + ' GB free'
              }}
            </span>
            @if (data.usage.storage.percent >= 100) {
              <span class="text-error font-medium">Storage full</span>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Admin for Plan Upgrades -->
    <div class="daisy-card bg-base-100 border border-base-300 shadow-sm p-6">
      <div class="flex items-center gap-4">
        <div
          class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0"
        >
          <i class="fa-solid fa-headset text-lg"></i>
        </div>
        <div>
          <h3 class="font-semibold text-base-content">
            Need more capacity or higher quotas?
          </h3>
          <p class="text-xs text-base-content/70 mt-0.5">
            To upgrade your workspace tier or adjust limits for instructors,
            live classes, students, or storage, please
            <strong>contact the admin team</strong>. Plan upgrades and
            adjustments are managed directly by administrators.
          </p>
        </div>
      </div>
    </div>
  }
</div>
